
import { Plus, Trash2, Check } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import type { ActivityFormData } from '../ActivityEditorPage.types';
import styles from './TypeSpecificFields.module.scss';

interface MultipleChoiceFieldsProps {
  options: string[];
  correctAnswerIndex: number;
  onChange: (updates: Partial<ActivityFormData>) => void;
  error?: string;
}

export function MultipleChoiceFields({
  options,
  correctAnswerIndex,
  onChange,
  error,
}: MultipleChoiceFieldsProps) {
  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    onChange({ options: newOptions });
  };

  const handleAddOption = () => {
    onChange({ options: [...options, ''] });
  };

  const handleRemoveOption = (index: number) => {
    if (options.length <= 2) {
      return; // Minimum 2 options required
    }

    const newOptions = options.filter((_, i) => i !== index);
    let newCorrectIndex = correctAnswerIndex;

    // Adjust correct answer index if needed
    if (index === correctAnswerIndex) {
      newCorrectIndex = 0; // Reset to first option
    } else if (index < correctAnswerIndex) {
      newCorrectIndex = correctAnswerIndex - 1;
    }

    onChange({
      options: newOptions,
      correctAnswerIndex: newCorrectIndex,
    });
  };

  const handleCorrectAnswerChange = (value: string) => {
    onChange({ correctAnswerIndex: parseInt(value, 10) });
  };

  return (
    <div className={styles.typeFields}>
      <h3 className={styles.typeFields__title}>Opciones de respuesta múltiple</h3>
      <p className={styles.typeFields__description}>
        Añade al menos 2 opciones y marca la respuesta correcta con el botón de radio.
      </p>

      <div className={styles.typeFields__list}>
        {options.map((option, index) => (
          <div key={index} className={styles.typeFields__optionItem}>
            <div className={styles.typeFields__optionRadio}>
              <input
                type="radio"
                name="correctAnswer"
                value={index}
                checked={correctAnswerIndex === index}
                onChange={(e) => handleCorrectAnswerChange(e.target.value)}
                className={styles.typeFields__radio}
              />
              {correctAnswerIndex === index && <Check className={styles.typeFields__checkIcon} />}
            </div>

            <div className={styles.typeFields__optionInput}>
              <Input
                value={option}
                onChange={(e) => handleOptionChange(index, e.target.value)}
                placeholder={`Opción ${index + 1}`}
              />
            </div>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              leftIcon={<Trash2 />}
              onClick={() => handleRemoveOption(index)}
              disabled={options.length <= 2}
              aria-label={`Eliminar opción ${index + 1}`}
            >
              <span className="sr-only">Eliminar</span>
            </Button>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        leftIcon={<Plus />}
        onClick={handleAddOption}
        className={styles.typeFields__addButton}
      >
        Añadir opción
      </Button>

      {error && <span className={styles.typeFields__error}>{error}</span>}
    </div>
  );
}
